import { LinksApiService } from '@adminServices';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NotificationService } from '@shared';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as LinksActions from './links.actions';

@Injectable()
export class LinksEffects {
  private actions$ = inject(Actions);
  private linksApi = inject(LinksApiService);
  private notification = inject(NotificationService);

  loadLinks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinksActions.loadLinks),
      switchMap(({ params }) =>
        this.linksApi.findAll(params).pipe(
          map((response) => LinksActions.loadLinksSuccess({
            links: response.items,
            pagination: {
              total: response.total,
              page: response.page,
              limit: response.limit,
              totalPages: response.totalPages,
            },
          })),
          catchError((error: any) => {
            this.notification.error('admin.notifications.load_links_error');
            return of(LinksActions.loadLinksFailure({ error: error.message || 'Failed to load links' }));
          }),
        ),
      ),
    ),
  );

  createLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinksActions.createLink),
      switchMap(({ linkData }) =>
        this.linksApi.create(linkData).pipe(
          map((link) => {
            this.notification.success('admin.notifications.link_created');
            return LinksActions.createLinkSuccess({ link });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.create_link_error');
            return of(LinksActions.createLinkFailure({ error: error.message || 'Failed to create link' }));
          }),
        ),
      ),
    ),
  );

  updateLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinksActions.updateLink),
      switchMap(({ id, linkData }) =>
        this.linksApi.update(id, linkData).pipe(
          map((link) => {
            this.notification.success('admin.notifications.link_updated');
            return LinksActions.updateLinkSuccess({ link });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.update_link_error');
            return of(LinksActions.updateLinkFailure({ error: error.message || 'Failed to update link' }));
          }),
        ),
      ),
    ),
  );

  deleteLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinksActions.deleteLink),
      switchMap(({ id }) =>
        this.linksApi.delete(id).pipe(
          map(() => {
            this.notification.success('admin.notifications.link_deleted');
            return LinksActions.deleteLinkSuccess({ id });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.delete_link_error');
            return of(LinksActions.deleteLinkFailure({ error: error.message || 'Failed to delete link' }));
          }),
        ),
      ),
    ),
  );

  uploadImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinksActions.uploadImage),
      switchMap(({ id, file }) =>
        this.linksApi.uploadImage(id, file).pipe(
          map(link => {
            this.notification.success('admin.notifications.image_uploaded');
            return LinksActions.uploadImageSuccess({ link });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.upload_image_error');
            return of(LinksActions.uploadImageFailure({ error: error.message || 'Failed to upload image' }));
          }),
        ),
      ),
    ),
  );

  deleteImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinksActions.deleteImage),
      switchMap(({ id }) =>
        this.linksApi.deleteImage(id).pipe(
          map(link => {
            this.notification.success('admin.notifications.image_deleted');
            return LinksActions.deleteImageSuccess({ link });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.delete_image_error');
            return of(LinksActions.deleteImageFailure({ error: error.message || 'Failed to delete image' }));
          }),
        ),
      ),
    ),
  );
}
